import {useState} from "react";
import {Heart} from "lucide-react";
import {Input} from "@/components/ui/input.tsx";
import {Button} from "@/components/ui/button.tsx";
import type Combatant from "@/encounter/model/Combatant.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {Field, FieldGroup, FieldLabel} from "@/components/ui/field.tsx";
import {Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {ButtonGroup} from "@/components/ui/button-group.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import {cn} from "@/lib/utils.ts";
import {Card} from "@/components/ui/card.tsx";
import {DamageTypesByGroup} from "@/spells/model/DamageTypes.ts";
import DiceRollsView from "./DiceRollsView.tsx";
import CombatantEditDialog from "./CombatantEditDialog.tsx";

type HealthTab = "damage" | "heal" | "enter";
export default function EditHealthDialog({combatant}: {combatant: Combatant}) {
    const {update} = useCombatants();
    const [tab, setTab] = useState<HealthTab>("damage");
    const [currentLife, setCurrentLife] = useState(String(combatant.currentLife));
    const [maxLife, setMaxLife] = useState(String(combatant.maxLife));
    const [base, setBase] = useState({currentLife: combatant.currentLife, maxLife: combatant.maxLife});
    const validate = () => {
        const current = Number(currentLife), maximum = Number(maxLife);
        return !currentLife.trim() || !maxLife.trim() || !Number.isSafeInteger(current) || !Number.isSafeInteger(maximum)
            || current < 0 || maximum < 1 || current > maximum
            ? "Bitte gib ganze Trefferpunkte ein: Maximum mindestens 1, aktuelle HP zwischen 0 und Maximum." : undefined;
    };
    const calculationCombatant = {...combatant, ...base};
    const changeTab = (next: HealthTab) => {
        if (next === tab || (next !== "enter" && validate())) return;
        setBase({currentLife: Number(currentLife), maxLife: Number(maxLife)});
        setTab(next);
    };
    return <CombatantEditDialog title="Trefferpunkte" description="Schaden und Heilung berechnen oder Trefferpunkte direkt eingeben." wide
        trigger={<button type="button" aria-label="Trefferpunkte bearbeiten" className="flex cursor-pointer items-center gap-2 hover:underline">
            <Heart className="size-5 flex-none"/><span className="font-semibold">{combatant.currentLife} / {combatant.maxLife}</span>
        </button>}
        onOpen={() => {
            setCurrentLife(String(combatant.currentLife)); setMaxLife(String(combatant.maxLife));
            setBase({currentLife: combatant.currentLife, maxLife: combatant.maxLife}); setTab("damage");
        }} validate={validate}
        onSave={() => update([{...combatant, currentLife: Number(currentLife), maxLife: Number(maxLife)}], "confirmed")}>
        <ButtonGroup aria-label="Trefferpunkte bearbeiten">
            <Button type="button" size="sm" variant="outline" aria-pressed={tab === "damage"} disabled={!!validate()}
                className={tab === "damage" ? "bg-red-800 hover:bg-red-700" : ""} onClick={() => changeTab("damage")}>Schaden</Button>
            <Button type="button" size="sm" variant="outline" aria-pressed={tab === "heal"} disabled={!!validate()}
                className={tab === "heal" ? "bg-emerald-700 hover:bg-emerald-600" : ""} onClick={() => changeTab("heal")}>Heilung</Button>
            <Button type="button" size="sm" variant="outline" aria-pressed={tab === "enter"}
                className={tab === "enter" ? "bg-accent hover:bg-accent/80" : ""} onClick={() => changeTab("enter")}>Eingeben</Button>
        </ButtonGroup>
        {tab === "damage" && <CalculateDamageView combatant={calculationCombatant} currentLife={Number(currentLife)} maxLife={Number(maxLife)}
            setCurrentLife={value => setCurrentLife(String(value))} setMaxLife={value => setMaxLife(String(value))}/>}
        {tab === "heal" && <CalculateHealView combatant={calculationCombatant} currentLife={Number(currentLife)}
            setCurrentLife={value => setCurrentLife(String(value))}/>}
        {tab === "enter" && <div className="rounded-md bg-surface p-4">
            <FieldGroup><div className="grid grid-cols-2 gap-4">
                <Field><FieldLabel htmlFor="currentLife">Aktuelle HP</FieldLabel>
                    <Input id="currentLife" value={currentLife} type="number" min={0} step={1} max={Number(maxLife)} required
                        onChange={event => setCurrentLife(event.target.value)}/></Field>
                <Field><FieldLabel htmlFor="maxLife">Maximale HP</FieldLabel>
                    <Input id="maxLife" value={maxLife} type="number" min={1} step={1} required
                        onChange={event => setMaxLife(event.target.value)}/></Field>
            </div></FieldGroup>
        </div>}
    </CombatantEditDialog>;
}
function CalculateHealView({combatant, currentLife, setCurrentLife}: {
    combatant: Combatant
    currentLife: number;
    setCurrentLife: (setCurrentLife: number) => void
}) {

    const [heal, setHeal] = useState<number>(0);

    const changeHeal = (value: number) => {
        setHeal(value);
        if (!Number.isSafeInteger(value) || value < 0) return;
        setCurrentLife(Math.max(0, Math.min(combatant.currentLife + value, combatant.maxLife)));
    };

    return (
        <>
            <Card className="p-3">
                <DiceRollsView setResult={changeHeal}/>
            </Card>
            <Card className="p-3">
                <FieldGroup>
                    <div className="grid grid-cols-2 gap-4">
                        <Field>
                            <FieldLabel htmlFor="healing">Heilung</FieldLabel>
                            <Input id="healing" value={heal} type="number" min={0} step={1} required
                                   onChange={(e) => changeHeal(Number(e.target.value))}/>
                        </Field>
                    </div>
                </FieldGroup>
            </Card>

            <Card className="p-6 flex flex-row gap-4 justify-between items-center">
                <div
                    className={"text-emerald-500"}>{Math.min(heal, combatant.maxLife - combatant.currentLife)} Heilung
                </div>
                <div className="flex items-center gap-2 text-sm font-medium">
                    <Heart className="size-4"/> {combatant.currentLife} &rarr; {currentLife}
                </div>
            </Card>
        </>
    )
}


function CalculateDamageView({combatant, currentLife, setCurrentLife, maxLife, setMaxLife}: {
    combatant: Combatant
    currentLife: number;
    setCurrentLife: (setCurrentLife: number) => void
    maxLife: number;
    setMaxLife: (setMaxLife: number) => void
}) {

    const [damage, setDamage] = useState<number>(0);
    const [multiplier, setMultiplier] = useState<number>(1);
    const [damageType, setDamageType] = useState<string>();
    const [reduceMaxLife, setReduceMaxLife] = useState<boolean>(false)

    const changeDamage = (nextDamage = damage, nextMultiplier = multiplier, nextReduce = reduceMaxLife) => {
        setDamage(nextDamage);
        setMultiplier(nextMultiplier);
        setReduceMaxLife(nextReduce);
        if (!Number.isSafeInteger(nextDamage) || nextDamage < 0) return;
        const actualDamage = Math.max(0, Math.floor(nextDamage * nextMultiplier));
        const maximum = Math.max(combatant.maxLife - (nextReduce ? actualDamage : 0), 1);
        setCurrentLife(Math.min(maximum, Math.max(combatant.currentLife - actualDamage, 0)));
        setMaxLife(maximum);
    };
    const updateDamageType = (newDamageType: string) => {
        const matches = (values?: string[]) => values?.some(value => value.includes(newDamageType));
        const nextMultiplier = matches(combatant.character.damageImmunitys) ? 0
            : matches(combatant.character.damageResistances) ? 0.5
            : matches(combatant.character.damageVulnerabilitys) ? 2 : 1;
        setDamageType(newDamageType);
        changeDamage(damage, nextMultiplier);
    };
    return (
        <>
            <Card className="p-3">
                <DiceRollsView setResult={value => changeDamage(value)}/>
            </Card>
            <Card className="p-3">
                <FieldGroup>
                    <div className="grid grid-cols-2 gap-4">
                        <Field>
                            <FieldLabel htmlFor="damage">Schaden</FieldLabel>
                            <Input id="damage" value={damage} type="number" min={0} step={1} required
                                   onChange={(e) => changeDamage(Number(e.target.value))}/>
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="type">Art</FieldLabel>
                            <Select value={damageType} onValueChange={updateDamageType}>
                                <SelectTrigger id="type">
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(DamageTypesByGroup).map(([key, values]) =>
                                        <SelectGroup key={key}>
                                            <SelectLabel>{key}</SelectLabel>
                                            {values.map(value => <SelectItem key={value}
                                                                             value={value}>{value}</SelectItem>)}
                                        </SelectGroup>)
                                    }
                                </SelectContent>
                            </Select>
                        </Field>

                        <Field>
                            <ButtonGroup>
                                <Button type="button" size="icon-sm" variant="outline"
                                        className={multiplier === 0 ? "bg-red-700 hover:bg-red-600" : ""}
                                        onClick={() => changeDamage(damage, 0)}>0 &times;</Button>
                                <Button type="button" size="icon-sm" variant="outline"
                                        className={multiplier === 0.25 ? "bg-orange-700 hover:bg-orange-600" : ""}
                                        onClick={() => changeDamage(damage, 0.25)}>&#xbc; &times;</Button>
                                <Button type="button" size="icon-sm" variant="outline"
                                        className={multiplier === 0.5 ? "bg-yellow-700 hover:bg-yellow-600" : ""}
                                        onClick={() => changeDamage(damage, 0.5)}>&#xbd; &times;</Button>
                                <Button type="button" size="icon-sm" variant="outline"
                                        className={multiplier === 1 ? "bg-sky-700 hover:bg-sky-600" : ""}
                                        onClick={() => changeDamage(damage, 1)}>1 &times;</Button>
                                <Button type="button" size="icon-sm" variant="outline"
                                        className={multiplier === 2 ? "bg-emerald-700 hover:bg-emerald-600" : ""}
                                        onClick={() => changeDamage(damage, 2)}>2 &times;</Button>
                            </ButtonGroup>
                        </Field>
                        <Field orientation="horizontal">
                            <Checkbox id="reduceMaxLife" checked={reduceMaxLife}
                                      onCheckedChange={checked => changeDamage(damage, multiplier, checked === true)}/>
                            <FieldLabel htmlFor="reduceMaxLife">Maximum verringern</FieldLabel>
                        </Field>
                    </div>
                </FieldGroup>
            </Card>

            <Card className="p-6 flex flex-row gap-4 justify-between items-center">
                <div className={cn(
                    "text-xl font-semibold",
                    multiplier === 0 ? "text-red-500" : "",
                    multiplier === 0.25 ? "text-orange-500" : "",
                    multiplier === 0.5 ? "text-yellow-500" : "",
                    multiplier === 1 ? "text-sky-500" : "",
                    multiplier === 2 ? "text-emerald-500" : ""
                )}>
                    {Math.min(Math.floor(damage * multiplier), combatant.currentLife)} {damageType} Schaden
                </div>

                <div className="flex-col items-start gap-2">
                    <div className="flex items-center gap-2 font-medium">
                        <Heart className="size-4"/> {combatant.currentLife} &rarr; {currentLife}
                    </div>
                    {reduceMaxLife &&
                        <div className="text-muted-foreground">
                            Max: {combatant.maxLife} &rarr; {maxLife}
                        </div>}
                </div>
            </Card>

            {combatant.character.armorClass &&
                <Card className="p-3 flex flex-row items-center">
                    <label className="font-semibold">Rüstung:&nbsp;
                        <span className="text-orange-700">{combatant.character.armorClass.value}</span>
                    </label>
                    {combatant.character.armorClass.info &&
                        <div className="capitalize leading-none">{combatant.character.armorClass.info}</div>}
                </Card>
            }

            <Card className="p-3 flex flex-row justify-between gap-2">
                {combatant.character.damageResistances &&
                    <div>
                        <div className="font-semibold">Resistenz</div>
                        <ul className="list-inside list-disc text-xs">
                            {combatant.character.damageResistances.map((value) =>
                                <li key={value}>
                                    {(damageType && value.indexOf(damageType) !== -1) ? <mark>{value}</mark> : value}
                                </li>)
                            }
                        </ul>
                    </div>
                }
                {combatant.character.damageVulnerabilitys &&
                    <div>
                        <div className="font-semibold">Verwundbar</div>
                        <ul className="list-inside list-disc text-xs">
                            {combatant.character.damageVulnerabilitys.map((value) =>
                                <li key={value}>
                                    {(damageType && value.indexOf(damageType) !== -1) ? <mark>{value}</mark> : value}
                                </li>)
                            }
                        </ul>
                    </div>
                }
                {combatant.character.damageImmunitys &&
                    <div className="shrink">
                        <div className="font-semibold">Immun</div>
                        <ul className="list-inside list-disc text-xs">
                            {combatant.character.damageImmunitys.map((value) =>
                                <li key={value}>
                                    {(damageType && value.indexOf(damageType) !== -1) ? <mark>{value}</mark> : value}
                                </li>)
                            }
                        </ul>
                    </div>
                }
            </Card>
        </>
    )
}
